import { IDynamicLinkedServiceTypeMetadata, ILinkedServiceTypeMetadata } from 'app/extensibility/metadatas/resource-type/linked-service-type-metadata';

import { Metamodel } from 'app/model/base/model';
import { OdbcSink } from 'app/model/pipeline/copysink/odbc.copysink';
import { format } from 'app/shared/utils/string.util';
import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { ConnectionAuthKind } from 'shell-models-lib';
import { BuiltInDataSourceOptionId, DataSourceOptionKind } from '@powerquery/mashup-editor-client';
import { GlobalVariables } from 'app/services/globals';

export const getOdbcLinkedServiceConfigControls: () => ControlMetadata[] = () => {
    return [
        {
            type: 'dynamic-akv-switcher',
            bind: 'connectionString',
            label: stringResources.ModelName_ConnectionString,
            enableAKVParameterization: true,
            hideAKVSwitcherInConnStrProps: true,
            child: {
                type: 'parameterizable',
                attribute: 'connectionString',
                child: {
                    type: 'textarea',
                    label: stringResources.ModelName_ConnectionString,
                    required: true,
                    isLabelVertical: true,
                    skipEscapeDescription: true,
                    description: stringResources.LinkedServiceODBCConnStringDesp,
                    validators: [
                        { type: 'required', errorMessage: '' }
                    ]
                },
            },
            description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template_ConnectionStringWithSample,
                format(stringResources.ModelName_LinkedServiceTemplate, stringResources.Odbc_DisplayText),
                'Driver={SQL Server};Server=Server.database.windows.net; Database=TestDatabase;')
        },
        {
            type: 'select-switch',
            bind: 'authenticationType',
            label: stringResources.ModelName_AuthenticationType,
            options: [
                {
                    label: stringResources.Basic_DisplayText,
                    value: 'Basic'
                },
                {
                    label: stringResources.Anonymous_DisplayText,
                    value: 'Anonymous'
                }
            ],
            required: true,
            validators: [
                { type: 'required', errorMessage: '' }
            ],
            isLabelVertical: true,
            defaultValue: 'Basic',
            cases: [
                {
                    caseValue: 'Basic',
                    children: [
                        {
                            type: 'parameterizable', child: {
                                type: 'textbox',
                                bind: 'userName',
                                label: stringResources.ModelName_UserName,
                                required: true,
                                validators: [
                                    { type: 'required', errorMessage: '' }
                                ],
                                isLabelVertical: true
                            }
                        },
                        {
                            type: 'dynamic-akv-switcher',
                            bind: 'password',
                            label: stringResources.ModelName_Password,
                            enableAKVParameterization: true,
                            hideAKVSwitcherInConnStrProps: true,
                            child: {
                                type: 'parameterizable',
                                attribute: 'password',
                                disableAddParameter: true,
                                child: {
                                    type: 'password',
                                    label: stringResources.ModelName_Password,
                                    required: true,
                                    isLabelVertical: true
                                },
                            },
                            validators: [
                                { type: 'required', errorMessage: '' }
                            ],
                            description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                                format(stringResources.ModelName_LinkedServiceTemplate, stringResources.Odbc_DisplayText),
                                stringResources.ModelName_Password)
                        }
                    ]
                }
            ]
        },
        {
            type: 'dynamic-akv-switcher',
            bind: 'credential',
            required: false,
            enableAKVParameterization: true,
            hideAKVSwitcherInConnStrProps: true,
            label: stringResources.ModelName_Credential,
            child: {
                type: 'parameterizable',
                attribute: 'credential',
                disableAddParameter: true,
                child: {
                    type: 'password',
                    label: stringResources.ModelName_Credential,
                    isLabelVertical: true
                }
            },
            description: format(stringResources.AzureKeyVaultSecretName_Tooltip_Template,
                format(stringResources.ModelName_LinkedServiceTemplate, stringResources.Odbc_DisplayText),
                stringResources.ModelName_Credential)
        }
    ];
};

export const odbcMetadata: IDynamicLinkedServiceTypeMetadata = async (injector) => {
    const metadata: ILinkedServiceTypeMetadata = {
        type: adfModelTypeMap.odbc.linkedServiceType,
        displayName: stringResources.Odbc_DisplayText,
        linkedServiceKind: 'table',
        icon: {
            className: 'svg-i-100x100-PlaceHolderDatabase'
        },
        isOnPremises: true,
        isFirstClass: true,
        supportFirstClassParameterExperience: true,
        supportedInTrident: true,
        supportAutoFillCopilotInput: true,
        isKnownInPipelineContext: true,
        pqoDataSourceOption: {
            kind: DataSourceOptionKind.BuiltIn,
            id: BuiltInDataSourceOptionId.Odbc
        },
        supportOPDG: GlobalVariables.enableOPDG,
        supportCopyJobSource: true,
        supportCopyJobSink: true,
        supportMultipleIncrementalColumns: true,
        supportedAuthKindsInTrident: [ConnectionAuthKind.Basic, ConnectionAuthKind.Anonymous],
        tableColumnNameQuotes: ['"', '"'],
        supportPreCopyScript: true,
        category: ['GenericProtocol'],
        supportDatasetV2: true,
        copySinkTypes: [Metamodel.modelFor(OdbcSink).name],
        copySourceTypes: [adfModelTypeMap.odbc.copySourceType],
        controls: getOdbcLinkedServiceConfigControls()
    };
    return metadata;
}
